import qrcode from "qrcode-generator";
import { detectLanguage } from "../lib/language-detect.mjs";
import { Room, RoomEvent, Track } from "livekit-client";

const elements = {
  agendaBlock: document.querySelector("#agendaBlock"),
  agendaList: document.querySelector("#agendaList"),
  audio: document.querySelector("#translatedAudio"),
  audioToggle: document.querySelector("#audioToggle"),
  broadcastDot: document.querySelector("#broadcastDot"),
  broadcastText: document.querySelector("#broadcastText"),
  channelCodeDisplay: document.querySelector("#channelCodeDisplay"),
  clipBadge: document.querySelector("#clipBadge"),
  dayInput: document.querySelector("#dayInput"),
  eventInfo: document.querySelector("#eventInfo"),
  eventName: document.querySelector("#eventName"),
  nextAgendaButton: document.querySelector("#nextAgendaButton"),
  refreshAgendaButton: document.querySelector("#refreshAgendaButton"),
  speakerLanguage: document.querySelector("#speakerLanguage"),
  listenerQr: document.querySelector("#listenerQr"),
  uploadStatus: document.querySelector("#uploadStatus"),
  copyLinkButton: document.querySelector("#copyLinkButton"),
  fullscreenButton: document.querySelector("#fullscreenButton"),
  listenerUrl: document.querySelector("#listenerUrl"),
  liveGrid: document.querySelector("#liveGrid"),
  lanePicker: document.querySelector("#lanePicker"),
  meter: document.querySelector("#meter"),
  meterCover: document.querySelector("#meterCover"),
  meterHint: document.querySelector("#meterHint"),
  meterPeak: document.querySelector("#meterPeak"),
  meterValue: document.querySelector("#meterValue"),
  nextSectionButton: document.querySelector("#nextSectionButton"),
  sectionNumber: document.querySelector("#sectionNumber"),
  sectionSince: document.querySelector("#sectionSince"),
  sectionTitleInput: document.querySelector("#sectionTitleInput"),
  sessionTime: document.querySelector("#sessionTime"),
  signalText: document.querySelector("#signalText"),
  sourceLive: document.querySelector("#sourceLive"),
  sourceSelect: document.querySelector("#sourceSelect"),
  sourceTranscript: document.querySelector("#sourceTranscript"),
  startButton: document.querySelector("#startButton"),
  startLabel: document.querySelector("#startLabel"),
  statusDot: document.querySelector("#statusDot"),
  statusText: document.querySelector("#statusText"),
  testInputButton: document.querySelector("#testInputButton"),
  toast: document.querySelector("#toast"),
};

const LANGUAGE_NAMES = { es: "Spanish", en: "English", pt: "Portuguese" };
const modeLabel = () => `Translation to ${selectedLanguages().map((code) => code.toUpperCase()).join(", ")}`;
// La cabina siempre trabaja sobre un evento: el código llega por la dirección (?c=) y los idiomas salen del evento.
const params = new URLSearchParams(window.location.search);
const EVENT_CODE = (params.get("c") ?? "").trim().toUpperCase();
let languageMode = "es";
// Qué tarjetas de traducción se muestran; las sesiones corren para todos los idiomas del evento.
let visibleLanes = new Set();
const CHANNEL_PATTERN = /^[A-Z0-9-]{4,16}$/;
const REPEAT_WARNING_COOLDOWN_MS = 20_000;
// Ni la API ni el evento "done" garantizan un corte por cada pausa hablada,
// así que insertamos el salto de línea nosotros cuando el habla se detiene un momento.
const PARAGRAPH_PAUSE_MS = 900;
// El idioma del orador cambia solo tras ver el mismo resultado en dos revisiones seguidas, para no oscilar.
const DETECTION_INTERVAL_MS = 1_500;
const DETECTION_CONFIRMATIONS = 2;
const DETECTION_WINDOW_CHARS = 220;
const encoder = new TextEncoder();

let running = false;
let lanes = [];
let sourceStream = null;
let originalPublication = null;
// Idioma que habla el orador, detectado solo a partir de la transcripción del original.
let speakerLanguage = null;
let speakerCandidate = { language: null, hits: 0 };
let speakerWindow = "";
let lastDetectionAt = 0;
let broadcastRoom = null;
let wakeLock = null;
let timer = null;
let sessionStartedAt = 0;
let lastRepeatWarningAt = 0;
// La traducción llega 1–3 s detrás del orador: lo que entra en este margen
// después de cerrar todavía pertenece a la sección que termina.
const SECTION_GRACE_MS = 4_000;
// El modelo retiene el final de la frase hasta que el orador vuelve a hablar:
// ese resto llega ya en la sección nueva y se devuelve a la que cerró.
const SECTION_TAIL_TIMEOUT_MS = 45_000;
const SENTENCE_END = /[.!?…]["'”»)\]]*$/;
let section = newSection(1);
let closingSection = false;
let tailSection = null;
let listenerBaseUrl = `${window.location.origin}/listener.html`;
// OpenAI cierra cada sesión a los 60 minutos: se renueva antes, aprovechando una pausa del orador.
const SESSION_ROTATE_AFTER_MS = 55 * 60_000;
const SESSION_ROTATE_DEADLINE_MS = 58 * 60_000;
const ROTATE_QUIET_DB = -45;
const ROTATE_QUIET_MS = 700;
const SESSION_TRACK_TIMEOUT_MS = 15_000;
const DISCONNECTED_GRACE_MS = 5_000;
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 20_000];
// Vigilante: con voz entrando y sin NINGÚN texto de OpenAI (ni original ni traducción) durante este tiempo,
// la sesión se da por muerta. Un canal que calla porque el orador habla su idioma sigue recibiendo el original.
const SILENT_SESSION_LIMIT_MS = 20_000;
const CHANNEL_ERROR_GRACE_MS = 3_000;
let reconnectingBroadcast = false;
const connectionLog = [];

// Registro de cortes y reconexiones: va al archivo de cada sección para explicar cualquier caída.
function logConnection(message) {
  connectionLog.push({ at: new Date(), message });
  if (connectionLog.length > 500) connectionLog.shift();
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function withTimeout(promise, ms, message) {
  return Promise.race([promise, sleep(ms).then(() => { throw new Error(message); })]);
}

function setStatus(state, text) {
  elements.statusDot.className = `status-dot ${state}`.trim();
  elements.statusText.textContent = text;
}

function setBroadcastStatus(state, text) {
  elements.broadcastDot.className = `status-dot ${state}`.trim();
  elements.broadcastText.textContent = text;
}

function showToast(message) {
  elements.toast.textContent = message;
  elements.toast.hidden = false;
  window.clearTimeout(showToast.timeout);
  showToast.timeout = window.setTimeout(() => {
    elements.toast.hidden = true;
  }, 8_000);
}

function renderTranscript(element, text) {
  element.textContent = text || "";
  element.scrollTop = element.scrollHeight;
}

function readStored(area, key) {
  try {
    return window[area].getItem(key) ?? "";
  } catch {
    return "";
  }
}

function removeStored(area, key) {
  try {
    window[area].removeItem(key);
  } catch {
    // Sin almacenamiento no hay nada que borrar.
  }
}

function writeStored(area, key, value) {
  try {
    window[area].setItem(key, value);
  } catch {
    // Sin almacenamiento el operador solo tiene que volver a escribir los datos.
  }
}

function selectedLanguages() {
  return languageMode.split(",");
}

// Sin sesión válida se vuelve al inicio, que pide el acceso y regresa aquí.
function goToLogin() {
  window.location.replace(`/?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
}

function lanePickerKey() {
  return `italk.lanes.${EVENT_CODE}`;
}

function applyLanguageLayout() {
  const languages = selectedLanguages();
  const shown = languages.filter((code) => visibleLanes.has(code));
  document.querySelectorAll("[data-lane]").forEach((card) => {
    card.hidden = !shown.includes(card.dataset.lane);
  });
  elements.liveGrid.style.setProperty("--lanes", String(Math.max(shown.length, 1)));
  elements.lanePicker.hidden = languages.length < 2;
  elements.lanePicker.querySelectorAll("button").forEach((button) => {
    button.setAttribute("aria-pressed", String(visibleLanes.has(button.dataset.pick)));
  });
}

function buildLanePicker() {
  const languages = selectedLanguages();
  let saved = [];
  try {
    saved = JSON.parse(localStorage.getItem(lanePickerKey()) ?? "[]");
  } catch {
    saved = [];
  }
  const kept = languages.filter((code) => saved.includes(code));
  visibleLanes = new Set(kept.length ? kept : languages);
  elements.lanePicker.replaceChildren(
    Object.assign(document.createElement("span"), { textContent: "Show" }),
    ...languages.map((code) => {
      const button = Object.assign(document.createElement("button"), { type: "button", textContent: LANGUAGE_NAMES[code] });
      button.dataset.pick = code;
      button.addEventListener("click", () => {
        if (visibleLanes.has(code) && visibleLanes.size === 1) return;
        if (visibleLanes.has(code)) visibleLanes.delete(code);
        else visibleLanes.add(code);
        try {
          localStorage.setItem(lanePickerKey(), JSON.stringify([...visibleLanes]));
        } catch {
          // Sin almacenamiento la elección solo dura mientras la pantalla esté abierta.
        }
        applyLanguageLayout();
      });
      return button;
    }),
  );
}

function createLane(language) {
  const card = document.querySelector(`[data-lane="${language}"]`);
  return {
    language,
    transcript: card.querySelector(".transcript"),
    badge: card.querySelector(".live-badge"),
    session: null,
    publication: null,
    voicedMsWithoutActivity: 0,
    reconnecting: false,
    rotating: false,
    disconnectTimer: 0,
    sessionCount: 0,
    lastChar: "",
    pendingSentence: "",
    recentSentences: [],
    eventCounts: {},
    audioConfig: null,
  };
}

function publishCaption(lane, delta, done = false) {
  if (!broadcastRoom || !delta) return;
  const payload = encoder.encode(JSON.stringify({ type: "caption", language: lane.language, delta, done }));
  broadcastRoom.localParticipant.publishData(payload, {
    reliable: true,
    topic: "ice-talk-caption",
  }).catch(() => {});
}

function startsParagraph(text, delta, lastDeltaAt, now) {
  // Solo entre palabras: el modelo a veces se frena en medio de una ("compartir|án").
  return Boolean(text) && !text.endsWith("\n") && /^\s/.test(delta) && now - lastDeltaAt > PARAGRAPH_PAUSE_MS;
}

// El `uid` identifica la sección desde que nace: es el id con el que se sube, así que recuperarla o reintentarla no la duplica.
function newSection(number) {
  return { uid: crypto.randomUUID(), number, startedAt: null, source: "", texts: {}, lastAt: {} };
}

function sectionHasText() {
  return Boolean(section.source || Object.values(section.texts).some(Boolean));
}

// Clave por canal y día local: al recargar la cabina se sigue con la numeración del día.
function sectionCounterKey() {
  return `italk.section.${currentChannel()}.${selectedDay()}`;
}

// Con evento cargado también cuentan las secciones que el servidor ya tiene de ese día.
function nextSectionNumber() {
  const saved = Number(readStored("localStorage", sectionCounterKey())) || 0;
  const onServer = Math.max(0, ...(eventState?.sections ?? []).filter((item) => item.day === selectedDay()).map((item) => item.seq));
  return Math.max(saved, onServer) + 1;
}

function renderSection() {
  renderTranscript(elements.sourceTranscript, section.source);
  document.querySelectorAll("[data-lane]").forEach((card) => {
    renderTranscript(card.querySelector(".transcript"), section.texts[card.dataset.lane] ?? "");
  });
  updateSectionControls();
}

function updateSectionControls() {
  elements.sectionNumber.textContent = String(section.number);
  elements.sectionSince.textContent = section.startedAt
    ? `since ${section.startedAt.toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" })}`
    : "no text yet";
  elements.sectionTitleInput.placeholder = `Section ${section.number} title (optional)`;
  elements.nextSectionButton.disabled = closingSection || (!running && !sectionHasText());
  elements.nextAgendaButton.disabled = elements.nextSectionButton.disabled || !currentAgendaItem();
  // Con agenda, «Next agenda item» es el botón principal; sin agenda queda solo «Split section».
  elements.nextAgendaButton.hidden = !dayAgenda().length;
  const last = agendaPointer === dayAgenda().length;
  elements.nextAgendaButton.textContent = closingSection ? "Saving section…" : last ? "Finish agenda" : "Next agenda item";
  elements.nextSectionButton.textContent = closingSection ? "Saving section…" : "Split section";
}

function appendToSection(key, delta, now) {
  const text = key === "source" ? section.source : section.texts[key] ?? "";
  let addition = delta;
  let paragraph = false;
  if (startsParagraph(text, delta, section.lastAt[key] ?? 0, now)) {
    addition = `\n${delta.trimStart()}`;
    paragraph = true;
  }
  if (key === "source") section.source = text + addition;
  else section.texts[key] = text + addition;
  section.lastAt[key] = now;
  if (!section.startedAt) {
    section.startedAt = new Date(now);
    updateSectionControls();
  }
  return { text: text + addition, paragraph, delta: paragraph ? delta.trimStart() : delta };
}

function appendSource(delta) {
  if (!delta) return;
  const { text } = appendToSection("source", delta, Date.now());
  renderTranscript(elements.sourceTranscript, text);
  elements.sourceLive.classList.add("active");
  elements.sourceLive.textContent = "LIVE";
  watchSpeakerLanguage(delta);
  mirrorOriginal(delta);
}

function watchSpeakerLanguage(delta) {
  speakerWindow = `${speakerWindow}${delta}`.slice(-DETECTION_WINDOW_CHARS);
  const now = Date.now();
  if (now - lastDetectionAt < DETECTION_INTERVAL_MS) return;
  lastDetectionAt = now;
  const found = detectLanguage(speakerWindow, lanes.map((lane) => lane.language));
  if (!found || found === speakerLanguage) {
    speakerCandidate = { language: null, hits: 0 };
    return;
  }
  speakerCandidate = speakerCandidate.language === found ? { language: found, hits: speakerCandidate.hits + 1 } : { language: found, hits: 1 };
  if (speakerCandidate.hits >= DETECTION_CONFIRMATIONS) setSpeakerLanguage(found);
}

function setSpeakerLanguage(language) {
  speakerLanguage = language;
  speakerCandidate = { language: null, hits: 0 };
  logConnection(`Speaker language detected: ${language.toUpperCase()}`);
  renderSpeakerLanguage();
  publishSpeakerLanguage();
}

function renderSpeakerLanguage() {
  elements.speakerLanguage.textContent = speakerLanguage ? `Speaker: ${LANGUAGE_NAMES[speakerLanguage]} (auto)` : "Speaker: detecting…";
  elements.speakerLanguage.hidden = !running;
}

function publishSpeakerLanguage() {
  if (!broadcastRoom || !speakerLanguage) return;
  const payload = encoder.encode(JSON.stringify({ type: "speaker", language: speakerLanguage }));
  broadcastRoom.localParticipant.publishData(payload, { reliable: true, topic: "ice-talk-caption" }).catch(() => {});
}

// En el idioma del orador la «traducción» sería inglés a inglés: en su lugar se muestra y se guarda el texto original.
function mirrorOriginal(delta) {
  const own = lanes.find((lane) => lane.language === speakerLanguage);
  if (!own) return;
  const result = appendToSection(own.language, delta, Date.now());
  if (result.paragraph) publishCaption(own, "\n");
  renderTranscript(own.transcript, result.text);
  own.badge.classList.add("active");
  own.badge.textContent = "LIVE";
  publishCaption(own, result.delta);
}

function startsNewSentence(text) {
  const first = text.trimStart()[0];
  return Boolean(first) && first !== first.toLowerCase();
}

// Devuelve la parte del fragmento que corresponde a la sección en curso.
function routeTail(language, delta) {
  if (!tailSection?.awaiting.has(language)) return delta;
  if (!tailSection.started.has(language)) {
    if (!delta.trim()) return "";
    if (startsNewSentence(delta)) {
      finishTail(language);
      return delta;
    }
    tailSection.started.add(language);
  }
  const end = delta.search(/[.!?…]/);
  const cut = end === -1 ? delta.length : end + 1;
  const target = tailSection.section;
  target.texts[language] = `${target.texts[language] ?? ""}${delta.slice(0, cut)}`;
  if (end !== -1) finishTail(language);
  return delta.slice(cut);
}

function finishTail(language) {
  tailSection.awaiting.delete(language);
  if (tailSection.awaiting.size === 0) finalizeTail();
}

function finalizeTail() {
  if (!tailSection) return;
  window.clearTimeout(tailSection.timer);
  const { section: closed, title, endedAt } = tailSection;
  tailSection = null;
  saveClosedSection(closed, title, endedAt);
}

async function fixSpacing(text) {
  if (!text.trim()) return { text, inserted: 0 };
  try {
    const result = await withTimeout(requestJson("/api/fix-spacing", { text }), 120_000, "timeout");
    return typeof result.text === "string" ? result : { text, inserted: 0 };
  } catch {
    return { text, inserted: 0 };
  }
}

// Solo el texto traducido tiene palabras pegadas; el original queda como llegó.
async function prepareSectionFile(target, title, endedAt) {
  const texts = {};
  let spacingFixes = 0;
  for (const [language, text] of Object.entries(target.texts)) {
    const fixed = await fixSpacing(text);
    texts[language] = fixed.text;
    spacingFixes += fixed.inserted;
  }
  return { ...buildSectionFile({ ...target, texts }, title, endedAt, spacingFixes), texts, spacingFixes };
}

async function saveClosedSection(closed, title, endedAt) {
  const hasText = Boolean(closed.source || Object.values(closed.texts).some(Boolean));
  if (!hasText) {
    releaseDraft(closed);
    showToast(`Section ${closed.number} had no text, so no file was downloaded.`);
    return;
  }
  showToast(`Saving section ${closed.number} (fixing spacing)…`);
  const file = await prepareSectionFile(closed, title, endedAt);
  if (eventState) {
    // Al entrar en la cola (que también vive en el navegador) el borrador ya no hace falta.
    enqueueUpload(closed, title, endedAt, file);
    releaseDraft(closed);
    return;
  }
  downloadText(file.filename, file.content);
  showToast(`Section ${closed.number} saved to Downloads.`);
}

function appendTranslation(lane, delta) {
  if (!delta || lane.language === speakerLanguage) return;
  // El modelo a veces omite el espacio al empezar una frase ("especialmenteVamos").
  if (/[a-záéíóúüñ]$/.test(lane.lastChar) && /^[A-ZÁÉÍÓÚÜÑ]/.test(delta)) delta = ` ${delta}`;
  lane.lastChar = delta.slice(-1);
  const current = routeTail(lane.language, delta);
  const tail = delta.slice(0, delta.length - current.length);
  if (tail) publishCaption(lane, tail);
  if (!current) return;
  const result = appendToSection(lane.language, current, Date.now());
  if (result.paragraph) publishCaption(lane, "\n");
  trackSentences(lane, result.delta);
  renderTranscript(lane.transcript, result.text);
  lane.badge.classList.add("active");
  lane.badge.textContent = "LIVE";
  publishCaption(lane, result.delta);
}

function normalizeForCompare(text) {
  return text.trim().toLowerCase().replace(/[.,;:!?¿¡"'…]/g, "").replace(/\s+/g, " ");
}

function checkRepeat(lane, sentence) {
  const normalized = normalizeForCompare(sentence);
  if (normalized.length < 20) return;

  const isRepeat = lane.recentSentences.some(
    (previous) => previous.includes(normalized) || normalized.includes(previous),
  );
  lane.recentSentences.push(normalized);
  if (lane.recentSentences.length > 8) lane.recentSentences.shift();

  if (isRepeat && Date.now() - lastRepeatWarningAt > REPEAT_WARNING_COOLDOWN_MS) {
    lastRepeatWarningAt = Date.now();
    showToast(`The ${LANGUAGE_NAMES[lane.language]} translation is repeating (possible echo). If you monitor the translation, use headphones and make sure the input is not capturing translated audio.`);
  }
}

function trackSentences(lane, delta) {
  lane.pendingSentence += delta;
  const sentences = lane.pendingSentence.split(/(?<=[.!?])\s+/);
  lane.pendingSentence = sentences.pop();
  sentences.forEach((sentence) => checkRepeat(lane, sentence));
}

function slugify(text) {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

function formatTime(date) {
  return date ? date.toLocaleTimeString("en", { hour: "2-digit", minute: "2-digit" }) : "—";
}

function connectionRange(target, endedAt) {
  const from = target.startedAt ?? endedAt;
  return connectionLog.filter((entry) => entry.at >= from && entry.at <= new Date(endedAt.getTime() + 60_000));
}

function connectionEntries(target, endedAt) {
  const entries = connectionRange(target, endedAt);
  if (!entries.length) return ["No dropouts or reconnections in this section."];
  return entries.map((entry) => `${entry.at.toLocaleTimeString("en")}  ${entry.message}`);
}

function buildSectionFile(target, title, endedAt, spacingFixes = 0) {
  const channel = currentChannel() || "NO-CHANNEL";
  const day = target.day ?? selectedDay();
  const number = String(target.number).padStart(2, "0");
  const languages = [...new Set([...Object.keys(target.texts), ...lanes.map((lane) => lane.language)])];
  const content = [
    "[ iTalk ] — Transcript",
    `Channel: ${channel}`,
    `Section: ${target.number}${title ? ` — ${title}` : ""}`,
    `Date: ${day} · from ${formatTime(target.startedAt)} to ${formatTime(endedAt)}`,
    `Mode: ${modeLabel()}`,
    "",
    "=== ORIGINAL ===",
    target.source.trim() || "(no content)",
    "",
    ...languages.flatMap((language) => [
      `=== ${language.toUpperCase()} CHANNEL (${LANGUAGE_NAMES[language]}) ===`,
      target.texts[language]?.trim() || "(no content)",
      "",
    ]),
    "=== DIAGNOSTICS (OpenAI events received during the broadcast) ===",
    ...lanes.flatMap((lane) => [
      `--- ${lane.language.toUpperCase()} channel ---`,
      `OpenAI sessions opened: ${lane.sessionCount} (including renewals and reconnections)`,
      ...Object.entries(lane.eventCounts).map(([type, count]) => `${type}: ${count}`),
      `Audio configuration: ${JSON.stringify(lane.audioConfig)}`,
    ]),
    `Spaces inserted between joined words: ${spacingFixes}`,
    `Input: ${elements.sourceSelect.selectedOptions[0]?.textContent ?? "unknown"}`,
    "",
    "=== CONNECTION LOG ===",
    ...connectionEntries(target, endedAt),
    "",
  ].join("\n");
  const titlePart = title ? `-${slugify(title)}` : "";
  return { filename: `italk-${channel}-${day}-section-${number}${titlePart}.txt`, content };
}

function downloadText(filename, content) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function broadcastSectionChange(number) {
  if (!broadcastRoom) return;
  const payload = encoder.encode(JSON.stringify({ type: "section", number }));
  broadcastRoom.localParticipant.publishData(payload, { reliable: true, topic: "ice-talk-caption" }).catch(() => {});
}

// Título de la sección abierta para los oyentes. Mensaje aparte del cambio de sección: reenviarlo no borra los subtítulos de nadie.
function publishSectionTitle() {
  if (!broadcastRoom) return;
  const payload = encoder.encode(JSON.stringify({ type: "title", title: elements.sectionTitleInput.value.trim() }));
  broadcastRoom.localParticipant.publishData(payload, { reliable: true, topic: "ice-talk-caption" }).catch(() => {});
}

async function closeSection({ advance = false } = {}) {
  if (closingSection) return;
  closingSection = true;
  const closing = section;
  closing.agendaPosition = currentAgendaItem()?.position ?? null;
  closing.day = selectedDay();
  const title = elements.sectionTitleInput.value.trim();
  // Mientras se cierra (espera de la traducción, corrección de espacios) sigue en el borrador: si la página se cae, se recupera.
  closingDrafts.add({ section: closing, title, endedAt: null });
  persistDraft();
  // El campo queda libre enseguida para escribir el título de la sección siguiente.
  elements.sectionTitleInput.value = "";
  elements.sectionTitleInput.placeholder = `Section ${closing.number + 1} title (optional)`;
  updateSectionControls();

  if (running) await new Promise((resolve) => window.setTimeout(resolve, SECTION_GRACE_MS));

  const endedAt = new Date();
  for (const record of closingDrafts) if (record.section === closing) record.endedAt = endedAt;
  const unfinished = running
    ? lanes.map((lane) => lane.language)
      .filter((language) => (closing.texts[language] ?? "").trim() && !SENTENCE_END.test(closing.texts[language].trim()))
    : [];
  finalizeTail();
  writeStored("localStorage", sectionCounterKey(), String(closing.number));
  section = newSection(closing.number + 1);
  for (const lane of lanes) {
    lane.pendingSentence = "";
    lane.recentSentences = [];
  }
  closingSection = false;
  if (advance && currentAgendaItem()) agendaPointer += 1;
  renderSection();
  renderAgenda();
  applyAgendaTitle();
  broadcastSectionChange(section.number);
  publishSectionTitle();

  if (unfinished.length) {
    tailSection = {
      section: closing,
      title,
      endedAt,
      awaiting: new Set(unfinished),
      started: new Set(),
      timer: window.setTimeout(finalizeTail, SECTION_TAIL_TIMEOUT_MS),
    };
    showToast(`Section ${section.number} has started. Section ${closing.number} will download after its final sentence finishes.`);
  } else {
    saveClosedSection(closing, title, endedAt);
  }
}

function handleRealtimeEvent(lane, message) {
  let event;
  try {
    event = JSON.parse(message.data);
  } catch {
    return;
  }
  lane.eventCounts[event.type] = (lane.eventCounts[event.type] ?? 0) + 1;
  if (event.type.endsWith("transcript.delta")) lane.voicedMsWithoutActivity = 0;
  // Todas las sesiones reciben el mismo audio: el original se toma de una sola.
  const isSourceLane = lane === lanes[0];

  switch (event.type) {
    case "session.input_transcript.delta":
      if (isSourceLane) appendSource(event.delta);
      break;
    case "session.output_transcript.delta":
      appendTranslation(lane, event.delta);
      break;
    case "session.created":
    case "session.updated":
      lane.audioConfig = event.session?.audio ?? null;
      setStatus("live", "Translating live");
      break;
    case "session.input_transcript.done":
      if (isSourceLane) appendSource("\n");
      break;
    case "session.output_transcript.done":
      checkRepeat(lane, lane.pendingSentence);
      lane.pendingSentence = "";
      appendTranslation(lane, "\n");
      publishCaption(lane, "\n", true);
      break;
    case "error":
      logConnection(`OpenAI (${lane.language.toUpperCase()}) reported an error: ${JSON.stringify(event.error ?? event).slice(0, 300)}`);
      showToast(`OpenAI (${LANGUAGE_NAMES[lane.language]}): ${event.error?.message ?? "the session reported an error."}`);
      break;
    default:
      console.debug("Unhandled translation event", lane.language, event);
      if (/fail|error/i.test(event.type)) {
        logConnection(`OpenAI (${lane.language.toUpperCase()}) sent ${event.type}: ${JSON.stringify(event.error ?? "").slice(0, 300)}`);
        showToast(`OpenAI (${LANGUAGE_NAMES[lane.language]}): ${event.type} — ${event.error?.message ?? "no details"}`);
      }
  }
}

async function loadAudioDevices() {
  if (!navigator.mediaDevices?.enumerateDevices) return;
  const selected = elements.sourceSelect.value;
  const devices = (await navigator.mediaDevices.enumerateDevices())
    .filter((device) => device.kind === "audioinput");

  elements.sourceSelect.replaceChildren();
  elements.sourceSelect.add(new Option("Default input", "default"));
  devices
    .filter((device) => device.deviceId !== "default")
    .forEach((device, index) => {
      elements.sourceSelect.add(new Option(device.label || `Audio input ${index + 1}`, device.deviceId));
    });
  elements.sourceSelect.add(new Option("Browser tab audio", "tab"));

  if ([...elements.sourceSelect.options].some((option) => option.value === selected)) {
    elements.sourceSelect.value = selected;
  }
}

async function captureAudio(source) {
  if (source === "tab") {
    const displayStream = await navigator.mediaDevices.getDisplayMedia({ audio: true, video: true });
    const audioTrack = displayStream.getAudioTracks()[0];
    if (!audioTrack) {
      displayStream.getTracks().forEach((track) => track.stop());
      throw new Error("Select a browser tab and enable “Share audio”.");
    }
    displayStream.getVideoTracks().forEach((track) => track.stop());
    return new MediaStream([audioTrack]);
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: source === "default" ? undefined : { exact: source },
      channelCount: 1,
      sampleRate: { ideal: 48_000 },
      sampleSize: { ideal: 16 },
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      latency: { ideal: 0 },
    },
    video: false,
  });
  stream.getAudioTracks()[0].contentHint = "speech";
  await loadAudioDevices();
  return stream;
}

function minimizeReceiverBuffer(receiver) {
  try {
    if ("playoutDelayHint" in receiver) receiver.playoutDelayHint = 0;
    if ("jitterBufferTarget" in receiver) receiver.jitterBufferTarget = 0;
  } catch {
    // Algunos navegadores exponen la propiedad pero no permiten modificarla.
  }
}

const METER_FLOOR_DB = -60;
const METER_RELEASE_DB_PER_S = 24;
const PEAK_HOLD_MS = 1_500;
const CLIP_DB = -1;
const meter = { context: null, frame: 0, level: -90, peak: -90, peakAt: 0, clipAt: 0, textAt: 0, quietSince: 0 };

function isQuiet() {
  return meter.quietSince > 0 && performance.now() - meter.quietSince > ROTATE_QUIET_MS;
}
let previewStream = null;

function toDb(amplitude) {
  return amplitude > 0 ? Math.max(-90, 20 * Math.log10(amplitude)) : -90;
}

function meterPercent(db) {
  return Math.min(100, Math.max(0, ((db - METER_FLOOR_DB) / -METER_FLOOR_DB) * 100));
}

function renderMeter(now) {
  elements.meterCover.style.width = `${100 - meterPercent(meter.level)}%`;
  elements.meterPeak.style.left = `calc(${meterPercent(meter.peak)}% - 2px)`;
  const clipping = now - meter.clipAt < 2_000;
  elements.clipBadge.hidden = !clipping;
  if (now - meter.textAt < 200) return;
  meter.textAt = now;

  const level = Math.round(meter.level);
  elements.meterValue.textContent = meter.level <= METER_FLOOR_DB ? "silence" : `${level} dBFS`;
  elements.meter.setAttribute("aria-valuenow", String(Math.max(METER_FLOOR_DB, level)));
  let hint = "";
  let tone = "";
  let signal = "Good";
  if (clipping) {
    [hint, tone, signal] = ["Clipping: lower the gain on the console or audio interface.", "bad", "Clipping"];
  } else if (meter.level < -50) {
    [hint, tone, signal] = ["No signal or silence. Check the input if the speaker is talking.", "warn", "Low"];
  } else if (meter.level < -36) {
    [hint, tone, signal] = ["Low: increase the gain if the speaker is talking.", "warn", "Low"];
  }
  elements.meterHint.textContent = hint;
  elements.meterHint.className = `meter-hint ${tone}`.trim();
  elements.signalText.textContent = signal;
}

function startMeter(stream) {
  stopMeter();
  const context = new AudioContext({ latencyHint: "interactive" });
  const analyser = context.createAnalyser();
  analyser.fftSize = 2048;
  context.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  Object.assign(meter, { context, level: -90, peak: -90, peakAt: 0, clipAt: 0, textAt: 0, quietSince: 0 });
  let last = performance.now();

  const tick = (now) => {
    const elapsed = (now - last) / 1_000;
    last = now;
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    let max = 0;
    for (const sample of samples) {
      sum += sample * sample;
      max = Math.max(max, Math.abs(sample));
    }
    const rms = toDb(Math.sqrt(sum / samples.length));
    const peak = toDb(max);
    // Sube al instante y baja despacio, como un vúmetro de consola.
    meter.level = Math.max(rms, meter.level - METER_RELEASE_DB_PER_S * elapsed);
    if (peak >= meter.peak) {
      meter.peak = peak;
      meter.peakAt = now;
    } else if (now - meter.peakAt > PEAK_HOLD_MS) {
      meter.peak = Math.max(peak, meter.peak - METER_RELEASE_DB_PER_S * elapsed);
    }
    if (peak > CLIP_DB) meter.clipAt = now;
    meter.quietSince = meter.level < ROTATE_QUIET_DB ? meter.quietSince || now : 0;
    renderMeter(now);
    meter.frame = requestAnimationFrame(tick);
  };
  meter.frame = requestAnimationFrame(tick);
}

function stopMeter() {
  cancelAnimationFrame(meter.frame);
  meter.context?.close().catch(() => {});
  meter.context = null;
  meter.quietSince = 0;
  elements.meterCover.style.width = "100%";
  elements.meterPeak.style.left = "0";
  elements.meterValue.textContent = "—";
  elements.clipBadge.hidden = true;
  elements.meterHint.textContent = "";
  elements.meterHint.className = "meter-hint";
  elements.signalText.textContent = "—";
}

async function startInputTest() {
  elements.testInputButton.disabled = true;
  try {
    previewStream = await captureAudio(elements.sourceSelect.value);
    previewStream.getAudioTracks()[0].addEventListener("ended", stopInputTest, { once: true });
    startMeter(previewStream);
    elements.testInputButton.textContent = "Stop test";
  } catch (error) {
    previewStream = null;
    showToast(error instanceof Error ? error.message : "Could not open the audio input.");
  } finally {
    elements.testInputButton.disabled = running;
  }
}

function stopInputTest() {
  if (!previewStream) return;
  previewStream.getTracks().forEach((track) => track.stop());
  previewStream = null;
  stopMeter();
  elements.testInputButton.textContent = "Test input";
}

function currentChannel() {
  return EVENT_CODE;
}

function renderChannelLink() {
  const channel = currentChannel();
  const valid = CHANNEL_PATTERN.test(channel);
  elements.channelCodeDisplay.textContent = valid ? channel : "—";
  elements.listenerUrl.textContent = valid
    ? `${listenerBaseUrl}?c=${encodeURIComponent(channel)}`
    : "Set a channel code";
  elements.copyLinkButton.disabled = !valid;
  if (valid) {
    const qr = qrcode(0, "M");
    qr.addData(elements.listenerUrl.textContent);
    qr.make();
    elements.listenerQr.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
    elements.listenerQr.hidden = false;
  } else {
    elements.listenerQr.hidden = true;
  }
}

async function requestJson(url, body) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error?.message ?? data.error ?? "Could not connect.");
    error.status = response.status;
    error.code = data.code;
    throw error;
  }
  return data;
}

async function createClientSecret(language) {
  const body = await requestJson("/api/session", { targetLanguage: language });
  if (!body.value) throw new Error("OpenAI did not return a session credential.");
  return body.value;
}

function updateAudienceCount() {
  const count = broadcastRoom?.remoteParticipants.size ?? 0;
  const label = count === 1 ? "1 listener connected" : `${count} listeners connected`;
  const publishing = lanes.some((lane) => lane.publication);
  setBroadcastStatus(publishing ? "live" : "connecting", publishing ? `Broadcasting · ${label}` : `Room ready · ${label}`);
}

// Identidad de esta pestaña: recargarla conserva el id (puede reconectar), otra pestaña o equipo tiene uno distinto.
function boothId() {
  let id = readStored("sessionStorage", "italk.boothId");
  if (!id) {
    id = crypto.randomUUID();
    writeStored("sessionStorage", "italk.boothId", id);
  }
  return id;
}

const requestOperatorToken = (takeover) =>
  requestJson("/api/livekit-token", { role: "operator", channel: currentChannel(), boothId: boothId(), takeover });

// Solo una cabina por canal: si ya hay otra publicando se pregunta si se toma el control (la otra se desconecta).
// En una reconexión automática no se pregunta: si el canal fue tomado por otra cabina, esta se detiene.
async function connectBroadcast({ interactive = true } = {}) {
  let credentials;
  try {
    credentials = await requestOperatorToken(false);
  } catch (error) {
    if (error.code !== "channel_busy" || !interactive) throw error;
    if (!window.confirm(`${error.message}\n\nTake over this channel? The other booth will be disconnected and stop broadcasting.`)) {
      throw new Error("Start cancelled: the channel already has an active booth.");
    }
    credentials = await requestOperatorToken(true);
  }
  const room = new Room({ adaptiveStream: true, dynacast: true });
  room.on(RoomEvent.ParticipantConnected, () => {
    updateAudienceCount();
    publishSectionTitle();
    publishSpeakerLanguage();
  });
  room.on(RoomEvent.ParticipantDisconnected, updateAudienceCount);
  room.on(RoomEvent.Reconnecting, () => {
    logConnection("Audience server: reconnecting");
    setBroadcastStatus("connecting", "Reconnecting audience…");
  });
  room.on(RoomEvent.Reconnected, () => {
    logConnection("Audience server: reconnected");
    updateAudienceCount();
  });
  room.on(RoomEvent.Disconnected, (reason) => {
    logConnection(`Audience server: disconnected (reason: ${reason ?? "unknown"})`);
    // LiveKit ya reintentó por su cuenta; si se rindió, se arma una conexión nueva.
    if (running && room === broadcastRoom) reconnectBroadcast();
    else if (!running) setBroadcastStatus("", "Audience server disconnected");
  });
  await room.connect(credentials.url, credentials.token);
  broadcastRoom = room;
  updateAudienceCount();
  return room;
}

async function reconnectBroadcast() {
  if (reconnectingBroadcast) return;
  reconnectingBroadcast = true;
  for (let attempt = 1; running; attempt += 1) {
    setBroadcastStatus("connecting", `Reconnecting audience · attempt ${attempt}`);
    logConnection(`Audience server: reconnection attempt ${attempt}`);
    try {
      await connectBroadcast({ interactive: false });
      for (const lane of lanes) {
        lane.publication = null;
        await publishLaneTrack(lane);
      }
      await publishOriginalTrack();
      publishSpeakerLanguage();
      showToast("Audience server reconnected.");
      break;
    } catch (error) {
      if (error?.code === "channel_busy") {
        // Otra cabina tomó el control: esta deja de transmitir (y sube lo que tenga) en vez de pelear por el canal.
        logConnection("Another booth took over this channel");
        reconnectingBroadcast = false;
        showToast("Another booth took over this channel. This booth stopped broadcasting and saved its section.");
        stopAndSave();
        return;
      }
      await sleep(RECONNECT_DELAYS_MS[Math.min(attempt - 1, RECONNECT_DELAYS_MS.length - 1)]);
    }
  }
  reconnectingBroadcast = false;
}

// Una sola publicación estable por canal: al cambiar de sesión de OpenAI se
// reemplaza el audio y los oyentes no tienen que volver a conectarse.
async function publishLaneTrack(lane) {
  const track = lane.session?.track;
  if (!broadcastRoom || !track) return;
  track.contentHint = "speech";
  if (lane.publication?.track) {
    await lane.publication.track.replaceTrack(track, true);
  } else {
    lane.publication = await broadcastRoom.localParticipant.publishTrack(track, {
      name: `translation-${lane.language}`,
      source: Track.Source.Unknown,
      dtx: false,
      red: true,
      forceStereo: false,
    });
  }
  updateAudienceCount();
}

// El audio del orador sin traducir, para quien escucha el idioma que se está hablando.
async function publishOriginalTrack() {
  const source = sourceStream?.getAudioTracks()[0];
  if (!broadcastRoom || !source) return;
  const track = source.clone();
  track.contentHint = "speech";
  originalPublication = await broadcastRoom.localParticipant.publishTrack(track, {
    name: "original",
    source: Track.Source.Unknown,
    dtx: false,
    red: true,
    forceStereo: false,
  });
}

function closeSession(session) {
  if (!session || session.closed) return;
  session.closed = true;
  session.channel?.close();
  session.pc.close();
}

async function openSession(lane) {
  const clientSecret = await createClientSecret(lane.language);
  const pc = new RTCPeerConnection();
  const session = { pc, channel: null, track: null, stream: null, connectedAt: 0, closed: false };
  try {
    pc.addTrack(sourceStream.getAudioTracks()[0], sourceStream);
    session.trackReady = new Promise((resolve) => {
      pc.ontrack = ({ track, streams, receiver }) => {
        minimizeReceiverBuffer(receiver);
        session.track = track;
        session.stream = streams[0] ?? new MediaStream([track]);
        resolve(session);
      };
    });
    session.channel = pc.createDataChannel("oai-events");
    // Solo la sesión activa escribe texto: durante una renovación la nueva espera su turno.
    session.channel.onmessage = (message) => {
      if (lane.session === session) handleRealtimeEvent(lane, message);
    };
    session.channel.onclose = () => {
      if (lane.session === session && !session.closed) reconnectLane(lane, "the OpenAI caption channel closed");
    };
    session.channel.onerror = (event) => {
      if (lane.session !== session || session.closed) return;
      logConnection(`Caption channel error (${lane.language.toUpperCase()}): ${event.error?.message ?? "no details"}`);
      // Un error puede ser pasajero: si el canal no sigue abierto pasado un momento, se reconecta.
      window.setTimeout(() => {
        if (lane.session === session && !session.closed && session.channel.readyState !== "open") {
          reconnectLane(lane, "the caption channel did not reopen after an error");
        }
      }, CHANNEL_ERROR_GRACE_MS);
    };
    pc.onconnectionstatechange = () => {
      if (lane.session === session) handleSessionState(lane, session);
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const sdpResponse = await fetch("https://api.openai.com/v1/realtime/translations/calls", {
      method: "POST",
      headers: { Authorization: `Bearer ${clientSecret}`, "Content-Type": "application/sdp" },
      body: offer.sdp,
    });
    if (!sdpResponse.ok) throw new Error(await sdpResponse.text());
    await pc.setRemoteDescription({ type: "answer", sdp: await sdpResponse.text() });
    await withTimeout(session.trackReady, SESSION_TRACK_TIMEOUT_MS, "OpenAI did not return audio in time.");
    lane.sessionCount += 1;
    return session;
  } catch (error) {
    closeSession(session);
    throw error;
  }
}

async function switchSession(lane, session) {
  const previous = lane.session;
  lane.session = session;
  session.connectedAt = Date.now();
  lane.voicedMsWithoutActivity = 0;
  logConnection(`OpenAI session (${lane.language.toUpperCase()}) ${previous ? "replaced by a new session" : "opened"}`);
  if (lane === lanes[0]) {
    elements.audio.srcObject = session.stream;
    elements.audio.play().catch(() => {});
  }
  await publishLaneTrack(lane);
  closeSession(previous);
}

function handleSessionState(lane, session) {
  const state = session.pc.connectionState;
  window.clearTimeout(lane.disconnectTimer);
  if (state !== "new" && state !== "connecting") logConnection(`OpenAI connection (${lane.language.toUpperCase()}): ${state}`);
  if (state === "connected") {
    setStatus("live", "Translating live");
  } else if (state === "disconnected") {
    // "disconnected" puede ser un corte breve de red que WebRTC recupera solo.
    setStatus("connecting", `Unstable OpenAI connection (${LANGUAGE_NAMES[lane.language]})…`);
    lane.disconnectTimer = window.setTimeout(() => {
      if (lane.session === session && session.pc.connectionState === "disconnected") {
        reconnectLane(lane, `the connection remained unstable for ${DISCONNECTED_GRACE_MS / 1000}s`);
      }
    }, DISCONNECTED_GRACE_MS);
  } else if (state === "failed" || state === "closed") {
    reconnectLane(lane, `the OpenAI connection changed to “${state}”`);
  }
}

async function reconnectLane(lane, reason = "connection dropout") {
  if (!running || lane.reconnecting) return;
  lane.reconnecting = true;
  logConnection(`Reconnecting ${lane.language.toUpperCase()} channel: ${reason}`);
  lane.rotating = false;
  window.clearTimeout(lane.disconnectTimer);
  lane.badge.textContent = "RECONNECTING";
  const name = LANGUAGE_NAMES[lane.language];
  for (let attempt = 1; running; attempt += 1) {
    setStatus("connecting", `Reconnecting to OpenAI (${name}) · attempt ${attempt}`);
    try {
      const session = await openSession(lane);
      if (!running) {
        closeSession(session);
        break;
      }
      await switchSession(lane, session);
      setStatus("live", "Translating live");
      lane.badge.textContent = "LIVE";
      logConnection(`${lane.language.toUpperCase()} channel reconnected on attempt ${attempt}`);
      showToast(`${name} translation reconnected.`);
      break;
    } catch (error) {
      logConnection(`Reconnection attempt ${attempt} for the ${lane.language.toUpperCase()} channel failed: ${error instanceof Error ? error.message : "no details"}`.slice(0, 300));
      await sleep(RECONNECT_DELAYS_MS[Math.min(attempt - 1, RECONNECT_DELAYS_MS.length - 1)]);
    }
  }
  lane.reconnecting = false;
}

async function rotateLane(lane) {
  lane.rotating = true;
  try {
    const next = await openSession(lane);
    const deadline = lane.session.connectedAt + SESSION_ROTATE_DEADLINE_MS;
    while (running && lane.rotating && !isQuiet() && Date.now() < deadline) await sleep(200);
    if (!running || !lane.rotating || lane.reconnecting) {
      closeSession(next);
      return;
    }
    await switchSession(lane, next);
    showToast(`${LANGUAGE_NAMES[lane.language]} translation session renewed (OpenAI closes it after 60 minutes).`);
  } catch (error) {
    // La sesión actual sigue activa; se reintenta más tarde (el tope es la hora de OpenAI).
    logConnection(`Could not renew the ${lane.language.toUpperCase()} channel session: ${error instanceof Error ? error.message : "no details"}`.slice(0, 300));
    lane.rotateRetryAt = Date.now() + 30_000;
  } finally {
    lane.rotating = false;
  }
}

function maintainSessions() {
  for (const lane of lanes) {
    if (!running || !lane.session || lane.reconnecting) continue;
    // Vigilante: cuenta los segundos con voz en la entrada mientras OpenAI no manda nada.
    if (meter.level > ROTATE_QUIET_DB && Date.now() - lane.session.connectedAt > 3_000) {
      lane.voicedMsWithoutActivity += 1_000;
      if (lane.voicedMsWithoutActivity >= SILENT_SESSION_LIMIT_MS) {
        lane.voicedMsWithoutActivity = 0;
        reconnectLane(lane, `no response from OpenAI for ${SILENT_SESSION_LIMIT_MS / 1000}s while voice was present at the input`);
        continue;
      }
    }
    if (lane.rotating) continue;
    if (Date.now() < (lane.rotateRetryAt ?? 0)) continue;
    if (Date.now() - lane.session.connectedAt > SESSION_ROTATE_AFTER_MS) rotateLane(lane);
  }
}

function startClock() {
  sessionStartedAt = Date.now();
  timer = window.setInterval(() => {
    const seconds = Math.floor((Date.now() - sessionStartedAt) / 1_000);
    const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
    const remainder = String(seconds % 60).padStart(2, "0");
    elements.sessionTime.textContent = `${minutes}:${remainder}`;
    maintainSessions();
  }, 1_000);
}

// ---------- Borrador de la sección en curso ----------
// La sección abierta (y las que se están cerrando) se guardan en el navegador cada pocos segundos:
// si la pestaña se cierra o se cuelga, al volver a abrir la cabina se recuperan y se suben.
const DRAFT_INTERVAL_MS = 3_000;
const closingDrafts = new Set();
let lastDraft = "";
let draftWarned = false;

function draftKey() {
  return `italk.draft.${EVENT_CODE}`;
}

function snapshotOf(target, title, endedAt) {
  return {
    uid: target.uid, number: target.number, day: target.day ?? selectedDay(), title,
    startedAt: target.startedAt?.toISOString() ?? null, endedAt: endedAt?.toISOString() ?? null,
    source: target.source, texts: target.texts, agendaPosition: target.agendaPosition ?? null,
  };
}

const hasContent = (target) => Boolean(target.source || Object.values(target.texts).some(Boolean));

function persistDraft() {
  if (!EVENT_CODE) return;
  const closing = [...closingDrafts].filter((record) => hasContent(record.section))
    .map((record) => snapshotOf(record.section, record.title, record.endedAt));
  const open = sectionHasText() && ![...closingDrafts].some((record) => record.section === section) ? snapshotOf({ ...section, agendaPosition: currentAgendaItem()?.position ?? null }, elements.sectionTitleInput.value.trim(), null) : null;
  const serialized = open || closing.length ? JSON.stringify({ savedAt: new Date().toISOString(), open, closing }) : "";
  if (serialized.replace(/"savedAt":"[^"]*"/, "") === lastDraft.replace(/"savedAt":"[^"]*"/, "")) return;
  lastDraft = serialized;
  if (!serialized) {
    removeStored("localStorage", draftKey());
    return;
  }
  try {
    window.localStorage.setItem(draftKey(), serialized);
    draftWarned = false;
  } catch {
    // Sin espacio en el navegador el borrador no se guarda: se avisa una vez para que el operador cierre la sección.
    if (!draftWarned) {
      draftWarned = true;
      showToast("The browser has no space left to keep the draft of this section. Close the section soon so it is saved to the server.");
    }
  }
}

function releaseDraft(closed) {
  for (const record of closingDrafts) if (record.section === closed) closingDrafts.delete(record);
  persistDraft();
}

// Sube lo que haya quedado de una sesión anterior. Cada sección recuperada pasa a ser una «sección que se está cerrando»:
// sigue en el borrador hasta que entra en la cola de subida, y su id es el del borrador, así que repetir la recuperación no duplica.
function recoverDrafts() {
  let saved = null;
  try {
    saved = JSON.parse(readStored("localStorage", draftKey()) || "null");
  } catch {
    saved = null;
  }
  const seen = new Set();
  const items = saved ? [...(saved.closing ?? []), saved.open]
    .filter((item) => item && (item.source || Object.values(item.texts ?? {}).some(Boolean)) && !seen.has(item.uid) && seen.add(item.uid)) : [];
  if (!items.length) {
    removeStored("localStorage", draftKey());
    return;
  }
  showToast(`Recovered ${items.length} unsaved section(s) from the previous session. Uploading to the server…`);
  const recovered = items.map((item) => ({
    section: {
      uid: item.uid, number: item.number, day: item.day, startedAt: item.startedAt ? new Date(item.startedAt) : null,
      source: item.source ?? "", texts: item.texts ?? {}, lastAt: {}, agendaPosition: item.agendaPosition ?? null,
    },
    title: item.title ?? "",
    endedAt: new Date(item.endedAt ?? saved.savedAt ?? Date.now()),
  }));
  for (const record of recovered) {
    closingDrafts.add(record);
    const counterKey = `italk.section.${EVENT_CODE}.${record.section.day}`;
    if (record.section.number > (Number(readStored("localStorage", counterKey)) || 0)) writeStored("localStorage", counterKey, String(record.section.number));
  }
  lastDraft = "";
  persistDraft();
  for (const record of recovered) saveClosedSection(record.section, record.title, record.endedAt);
}

window.setInterval(persistDraft, DRAFT_INTERVAL_MS);

// ---------- Evento, agenda y subida al servidor ----------
const UPLOAD_QUEUE_KEY = "italk.uploadQueue";
const UPLOAD_RETRY_MS = 30_000;
let eventState = null;
let agendaPointer = 0;
let autoTitle = "";
let flushing = false;
let uploadRetryTimer = 0;
let uploadMessage = "";

function todayLocal() {
  return new Date().toLocaleDateString("sv-SE");
}

function selectedDay() {
  return elements.dayInput.value || todayLocal();
}

function dayAgenda() {
  return eventState?.days.find((entry) => entry.day === selectedDay())?.agenda ?? [];
}

function currentAgendaItem() {
  return dayAgenda()[agendaPointer - 1] ?? null;
}

function pointToFirstPending() {
  const agenda = dayAgenda();
  const index = agenda.findIndex((item) => !eventState.done.has(item.id));
  agendaPointer = agenda.length ? (index === -1 ? agenda.length + 1 : index + 1) : 0;
  applyAgendaTitle();
}

// El título del punto actual se propone solo si el operador no escribió otro.
function applyAgendaTitle() {
  const title = currentAgendaItem()?.title ?? "";
  if (!elements.sectionTitleInput.value || elements.sectionTitleInput.value === autoTitle) {
    elements.sectionTitleInput.value = title;
    autoTitle = title;
  }
  publishSectionTitle();
}

function renderAgenda() {
  const agenda = dayAgenda();
  elements.agendaBlock.hidden = !agenda.length;
  elements.agendaList.replaceChildren(...agenda.map((item, index) => {
    const done = eventState.done.has(item.id);
    const current = index + 1 === agendaPointer;
    const row = document.createElement("li");
    row.className = current ? "current" : done ? "done" : "";
    const button = document.createElement("button");
    button.type = "button";
    for (const [className, text] of [["pos", String(item.position)], ["time", item.startTime ?? ""], ["title", item.title], ["state", current ? "CURRENT" : done ? "SAVED" : ""]]) {
      const cell = document.createElement("span");
      cell.className = className;
      cell.textContent = text;
      button.append(cell);
    }
    button.addEventListener("click", () => {
      agendaPointer = item.position;
      renderAgenda();
      applyAgendaTitle();
      updateSectionControls();
    });
    // Solo se mueven puntos pendientes: los guardados quedan atados a su posición en el servidor.
    const movable = (other) => !done && other && !eventState.done.has(other.id);
    const moves = document.createElement("span");
    moves.className = "agenda-moves";
    for (const [label, text, step] of [["Move up", "↑", -1], ["Move down", "↓", 1]]) {
      const move = document.createElement("button");
      move.type = "button";
      move.textContent = text;
      move.setAttribute("aria-label", label);
      move.disabled = reorderingAgenda || !movable(agenda[index + step]);
      move.addEventListener("click", () => moveAgendaItem(index, step));
      moves.append(move);
    }
    row.append(button, moves);
    return row;
  }));
  updateSectionControls();
}

let reorderingAgenda = false;

// Reordena sin salir de la cabina: guarda la agenda en el servidor y vuelve a leerla; la transmisión y la sección abierta no se tocan.
async function moveAgendaItem(index, step) {
  if (reorderingAgenda || !eventState) return;
  const entry = eventState.days.find((candidate) => candidate.day === selectedDay());
  if (!entry || !entry.agenda[index + step]) return;
  // Las filas conservan su identificador por posición: el puntero acompaña al punto que se mueve.
  const from = index + 1;
  const to = index + step + 1;
  const pointer = agendaPointer === from ? to : agendaPointer === to ? from : agendaPointer;
  const items = entry.agenda.map((item) => ({ title: item.title, startTime: item.startTime ?? null, speakers: item.speakers ?? null }));
  [items[index], items[index + step]] = [items[index + step], items[index]];
  reorderingAgenda = true;
  renderAgenda();
  try {
    const days = eventState.days.map((candidate) => ({
      day: candidate.day,
      items: candidate.day === entry.day ? items : candidate.agenda.map((item) => ({ title: item.title, startTime: item.startTime ?? null, speakers: item.speakers ?? null })),
    }));
    const response = await fetch(`/api/events/${eventState.id}/agenda`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ days }),
    });
    if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error?.message ?? `Error ${response.status}`);
    await refreshAgenda(pointer);
  } catch (error) {
    showToast(`Could not reorder the agenda: ${error instanceof Error ? error.message : "no details"}`);
  } finally {
    reorderingAgenda = false;
    renderAgenda();
  }
}

// Vuelve a leer eventos y agenda del servidor (p. ej. tras editarla en otra pestaña) sin tocar la transmisión ni el texto en curso.
async function refreshAgenda(pointer = agendaPointer) {
  const detail = await getJson(`/api/events/by-code/${encodeURIComponent(EVENT_CODE)}`);
  const listing = await getJson(`/api/events/${detail.id}/sections`);
  eventState.days = detail.days;
  eventState.done = new Set(listing.sections.map((item) => item.agendaItemId).filter(Boolean));
  agendaPointer = Math.min(pointer, dayAgenda().length + 1);
  applyAgendaTitle();
  renderEvent();
}

function renderEvent() {
  if (!eventState) {
    elements.agendaBlock.hidden = true;
    updateSectionControls();
  } else {
    const agenda = dayAgenda();
    elements.eventName.textContent = `${eventState.name} · ${eventState.code}`;
    elements.eventInfo.textContent = agenda.length
      ? `${agenda.length} agenda ${agenda.length === 1 ? "item" : "items"} on ${selectedDay()}. Sections are saved to the server when closed.`
      : `There is no agenda for ${selectedDay()}. Sections will still be saved under that day.`;
    renderAgenda();
  }
  renderUploadStatus();
}

async function getJson(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error?.message ?? data.error ?? `Error ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return data;
}

// Día inicial: el de la dirección (?d=), si no hoy cuando está en la agenda, si no el próximo día con agenda.
function initialDay(days) {
  const requested = params.get("d") ?? "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(requested)) return requested;
  const today = todayLocal();
  const dates = days.map((entry) => entry.day);
  return dates.includes(today) ? today : dates.find((day) => day > today) ?? dates.at(-1) ?? today;
}

function blockWithError(message) {
  eventState = null;
  elements.eventName.textContent = "Could not open the event";
  elements.eventInfo.textContent = message;
  elements.startButton.disabled = true;
  renderEvent(message);
}

async function loadEvent() {
  if (!EVENT_CODE) {
    window.location.replace("/");
    return;
  }
  try {
    const detail = await getJson(`/api/events/by-code/${encodeURIComponent(EVENT_CODE)}`);
    const listing = await getJson(`/api/events/${detail.id}/sections`);
    const known = detail.languages.filter((language) => language in LANGUAGE_NAMES);
    if (!known.length) {
      blockWithError("The event has no language the booth can translate to. Update its languages on the Events screen.");
      return;
    }
    languageMode = known.join(",");
    buildLanePicker();
    eventState = {
      id: detail.id, name: detail.name, code: detail.channelCode, days: detail.days,
      sections: listing.sections.map((item) => ({ day: item.day, seq: item.seq })),
      done: new Set(listing.sections.map((item) => item.agendaItemId).filter(Boolean)),
    };
    document.title = `[ iTalk ] — Booth · ${detail.name}`;
    elements.dayInput.value = initialDay(detail.days);
    applyLanguageLayout();
    pointToFirstPending();
    renderEvent();
    elements.startButton.disabled = false;
    recoverDrafts();
    if (!running && !sectionHasText()) {
      section = newSection(nextSectionNumber());
      updateSectionControls();
    }
  } catch (error) {
    if (error.status === 401) {
      goToLogin();
      return;
    }
    blockWithError(error.status === 404 ? `No event exists with code ${EVENT_CODE}.` : error instanceof Error ? error.message : "Could not load the event.");
  }
}

let uploadQueue = loadUploadQueue();

function loadUploadQueue() {
  try {
    const parsed = JSON.parse(readStored("localStorage", UPLOAD_QUEUE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveUploadQueue() {
  writeStored("localStorage", UPLOAD_QUEUE_KEY, JSON.stringify(uploadQueue));
}

function renderUploadStatus() {
  const pending = uploadQueue.length;
  elements.uploadStatus.classList.toggle("pending", pending > 0);
  elements.uploadStatus.textContent = pending
    ? `${pending} ${pending === 1 ? "section" : "sections"} waiting to upload${uploadMessage ? ` — ${uploadMessage}` : ""}. Retrying automatically every ${UPLOAD_RETRY_MS / 1000}s.`
    : eventState && uploadMessage ? uploadMessage : "";
}

function enqueueUpload(closed, title, endedAt, file) {
  const languages = [...new Set([...Object.keys(file.texts), ...lanes.map((lane) => lane.language)])];
  const entries = connectionRange(closed, endedAt);
  const id = closed.uid ?? crypto.randomUUID();
  if (uploadQueue.some((entry) => entry.id === id)) return;
  uploadQueue.push({
    id,
    eventId: eventState.id,
    filename: file.filename,
    backedUp: false,
    position: closed.agendaPosition,
    body: {
      id: "",
      day: closed.day ?? selectedDay(),
      title: title.slice(0, 200),
      startedAt: closed.startedAt?.toISOString(),
      endedAt: endedAt.toISOString(),
      languages: languages.length ? languages.slice(0, 3) : ["es"],
      texts: file.texts,
      source: closed.source,
      content: file.content,
      seq: closed.number,
      agendaItemPosition: closed.agendaPosition ?? undefined,
      spacingFixes: file.spacingFixes,
      connectionIncidents: entries.length,
      connectionLog: entries.slice(-500).map((entry) => `${entry.at.toLocaleTimeString("en")}  ${entry.message}`.slice(0, 600)),
      mode: modeLabel(),
    },
  });
  uploadQueue[uploadQueue.length - 1].body.id = id;
  saveUploadQueue();
  showToast(`Section ${closed.number} closed; uploading to the server…`);
  renderUploadStatus();
  flushUploads();
}

async function postSection(entry, body) {
  return fetch(`/api/events/${entry.eventId}/sections`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(60_000),
  });
}

// Reintentable: sin red, errores del servidor, límite de uso o clave aún sin escribir. El resto es definitivo.
const isTransient = (status) => status >= 500 || status === 408 || status === 429 || status === 401;

async function uploadEntry(entry) {
  let response = await postSection(entry, entry.body);
  if (response.status === 409 && entry.body.seq) {
    // El número ya está tomado en el servidor: que lo asigne él (el archivo local conserva el de la cabina).
    const { seq, ...withoutSeq } = entry.body;
    response = await postSection(entry, withoutSeq);
  }
  if (response.ok) return { ok: true, data: await response.json().catch(() => ({})) };
  const data = await response.json().catch(() => ({}));
  return { ok: false, transient: isTransient(response.status), status: response.status, message: data.error?.message ?? data.error ?? `Error ${response.status}` };
}

function backupLocally(entry, reason) {
  if (entry.backedUp) return;
  entry.backedUp = true;
  downloadText(entry.filename, entry.body.content);
  saveUploadQueue();
  showToast(`${reason} A copy was downloaded: ${entry.filename}`);
}

async function flushUploads() {
  if (flushing) return;
  flushing = true;
  window.clearTimeout(uploadRetryTimer);
  uploadMessage = "";
  try {
    while (uploadQueue.length) {
      const entry = uploadQueue[0];
      let result;
      try {
        result = await uploadEntry(entry);
      } catch {
        result = { ok: false, transient: true, message: "no connection to the server" };
      }
      if (result.ok) {
        uploadQueue.shift();
        saveUploadQueue();
        const item = eventState?.id === entry.eventId ? eventState.days.find((entry2) => entry2.day === entry.body.day)?.agenda.find((point) => point.position === entry.position) : null;
        if (item) eventState.done.add(item.id);
        eventState?.sections.push({ day: entry.body.day, seq: result.data.section?.seq ?? entry.body.seq });
        uploadMessage = `Section ${result.data.section?.seq ?? entry.body.seq} saved to the server.`;
        renderAgenda();
        continue;
      }
      uploadMessage = result.status === 401 ? "your session expired: open the home page in another tab and sign in" : result.message;
      if (result.transient) {
        backupLocally(entry, `Upload failed (${uploadMessage}).`);
        break;
      }
      // Definitivo (p. ej. el evento ya no existe): se guarda copia local y se descarta para no trabar la cola.
      backupLocally(entry, `The server rejected the section (${result.message}).`);
      uploadQueue.shift();
      saveUploadQueue();
    }
  } finally {
    flushing = false;
    renderUploadStatus();
    if (uploadQueue.length) uploadRetryTimer = window.setTimeout(flushUploads, UPLOAD_RETRY_MS);
  }
}

window.addEventListener("online", () => { if (uploadQueue.length) flushUploads(); });

function setInputsDisabled(disabled) {
  elements.testInputButton.disabled = disabled;
  elements.sourceSelect.disabled = disabled;
  elements.dayInput.disabled = disabled;
}

async function startSession() {
  if (!eventState) {
    showToast("The event has not loaded yet.");
    return;
  }
  stopInputTest();
  const languages = selectedLanguages();
  lanes = languages.map(createLane);
  setStatus("connecting", "Connecting to OpenAI…");
  setBroadcastStatus("connecting", "Connecting to the audience server…");
  elements.startButton.disabled = true;

  try {
    const [stream] = await Promise.all([
      captureAudio(elements.sourceSelect.value),
      connectBroadcast(),
    ]);
    sourceStream = stream;
    elements.audio.muted = !elements.audioToggle.checked;
    const sessions = await Promise.all(lanes.map(openSession));
    await Promise.all(lanes.map((lane, index) => switchSession(lane, sessions[index])));
    await publishOriginalTrack();

    sourceStream.getAudioTracks()[0].addEventListener("ended", stopAndSave, { once: true });
    startMeter(sourceStream);
    startClock();
    if ("wakeLock" in navigator) wakeLock = await navigator.wakeLock.request("screen").catch(() => null);

    running = true;
    renderSpeakerLanguage();
    setStatus("live", "Translating live");
    elements.startButton.classList.add("stop");
    elements.startLabel.textContent = "Stop broadcast";
    setInputsDisabled(true);
    updateSectionControls();
  } catch (error) {
    stopSession();
    setStatus("error", "Could not start");
    setBroadcastStatus("error", "Audience server unavailable");
    showToast(error instanceof Error ? error.message : "Could not start the translation.");
  } finally {
    elements.startButton.disabled = false;
  }
}

function stopSession() {
  // Primero: así los manejadores de corte no intentan reconectar lo que se cierra a propósito.
  running = false;
  finalizeTail();
  window.clearInterval(timer);
  timer = null;
  for (const lane of lanes) {
    window.clearTimeout(lane.disconnectTimer);
    lane.rotating = false;
    closeSession(lane.session);
    lane.session = null;
    lane.publication = null;
    lane.pendingSentence = "";
    lane.recentSentences = [];
    lane.badge.classList.remove("active");
    lane.badge.textContent = "STOPPED";
  }
  stopMeter();
  sourceStream?.getTracks().forEach((track) => track.stop());
  sourceStream = null;
  originalPublication = null;
  speakerLanguage = null;
  speakerCandidate = { language: null, hits: 0 };
  speakerWindow = "";
  renderSpeakerLanguage();
  broadcastRoom?.disconnect();
  broadcastRoom = null;
  lastRepeatWarningAt = 0;
  wakeLock?.release().catch(() => {});
  wakeLock = null;
  elements.audio.srcObject = null;
  elements.startButton.classList.remove("stop");
  elements.startLabel.textContent = "Start broadcast";
  setInputsDisabled(false);
  elements.sourceLive.classList.remove("active");
  elements.sourceLive.textContent = "STOPPED";
  setStatus("", "Ready to connect");
  updateSectionControls();
}

// Al detener, la sección abierta se cierra y se sube sola: detener no debe perder lo traducido hasta ese momento.
let stopping = false;
async function stopAndSave() {
  if (stopping) return;
  stopping = true;
  elements.startButton.disabled = true;
  try {
    if (running && sectionHasText()) {
      elements.startLabel.textContent = "Saving section…";
      await closeSection();
    }
  } catch (error) {
    // Pase lo que pase se detiene: el borrador conserva el texto si el cierre falló.
    showToast(error instanceof Error ? error.message : "The section could not be closed.");
  } finally {
    stopSession();
    elements.startButton.disabled = false;
    stopping = false;
  }
}

elements.startButton.addEventListener("click", () => {
  if (running) stopAndSave();
  else startSession();
});
elements.audioToggle.addEventListener("change", () => {
  elements.audio.muted = !elements.audioToggle.checked;
  if (elements.audioToggle.checked) elements.audio.play().catch(() => {});
});
elements.copyLinkButton.addEventListener("click", async () => {
  await navigator.clipboard.writeText(elements.listenerUrl.textContent).catch(() => {});
  showToast("Audience link copied.");
});
elements.fullscreenButton.addEventListener("click", async () => {
  if (document.fullscreenElement) await document.exitFullscreen();
  else await document.documentElement.requestFullscreen();
});
elements.nextSectionButton.addEventListener("click", () => closeSection());
let titleTimer = 0;
elements.sectionTitleInput.addEventListener("input", () => {
  window.clearTimeout(titleTimer);
  titleTimer = window.setTimeout(publishSectionTitle, 400);
});
elements.refreshAgendaButton.addEventListener("click", async () => {
  elements.refreshAgendaButton.disabled = true;
  try {
    await refreshAgenda();
    showToast("Agenda updated.");
  } catch (error) {
    showToast(`Could not refresh the agenda: ${error instanceof Error ? error.message : "no details"}`);
  } finally {
    elements.refreshAgendaButton.disabled = false;
  }
});
elements.nextAgendaButton.addEventListener("click", () => closeSection({ advance: true }));
elements.dayInput.addEventListener("change", () => {
  writeStored("localStorage", "italk.day", elements.dayInput.value);
  if (eventState) pointToFirstPending();
  renderEvent();
  if (!running && !sectionHasText()) {
    section = newSection(nextSectionNumber());
    updateSectionControls();
  }
});
elements.testInputButton.addEventListener("click", () => {
  if (previewStream) stopInputTest();
  else startInputTest();
});
elements.sourceSelect.addEventListener("change", () => {
  if (!previewStream) return;
  stopInputTest();
  startInputTest();
});
navigator.mediaDevices?.addEventListener("devicechange", loadAudioDevices);
// Cerrar la pestaña corta la transmisión; con transmisión activa se pide confirmación antes.
window.addEventListener("beforeunload", (event) => {
  if (running) event.preventDefault();
});
window.addEventListener("pagehide", () => {
  persistDraft();
  stopSession();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") persistDraft();
});

elements.startLabel.textContent = "Start broadcast";
elements.dayInput.value = todayLocal();
applyLanguageLayout();
renderChannelLink();
renderEvent();
section = newSection(nextSectionNumber());
updateSectionControls();
if (uploadQueue.length) {
  renderUploadStatus();
  flushUploads();
}
loadEvent();
fetch("/api/config")
  .then((response) => response.json())
  .then((config) => {
    if (config.listenerUrl) listenerBaseUrl = config.listenerUrl;
    renderChannelLink();
  })
  .catch(() => {});
loadAudioDevices().catch(() => {});
